import {
  type Cascade,
  type CascadeLevel,
  type CascadeNode,
  MAX_CASCADE_LEVELS,
  newCascadeId,
} from '../elements/fieldConfig';

/**
 * Turns a spreadsheet into a cascade.
 *
 * THE FORMAT: one column per tier, one row per path, the header row naming the tiers.
 *
 *     Country,State,District
 *     India,Gujarat,Ahmedabad
 *     ,,Vadodara
 *     ,Maharashtra,Pune
 *
 * The COLUMN COUNT is the level count — three columns build three dropdowns, five build five.
 * Nothing here is fixed at a particular width.
 *
 * WHY THIS SHAPE AND NOT A PARENT/CHILD ID TABLE. A parent-id format is smaller and unambiguous,
 * and it is also unusable: it asks an admin to invent and maintain identifiers by hand. This layout
 * is the one anybody can export from any source, read at a glance, and correct in place — and the
 * header row doubles as the level mapping, so there is no mapping step to get wrong.
 *
 * WHY THE PARSING LIVES IN THE FRONTEND. `QuestionnaireField.ConfigJson` is stored as opaque jsonb
 * that the backend deliberately never parses, which makes `fieldConfig.ts` the single definition of
 * this shape in the product. Parsing server-side would create a second definition free to drift
 * from it. It also keeps the sheet — which may hold a customer's branch or geography data — inside
 * the browser, and needs no endpoint, no upload and no stored file.
 *
 * WHY NO SPREADSHEET LIBRARY. Excel puts TAB-separated text on the clipboard, so a paste and a
 * saved CSV are the same problem: split on a delimiter, respect quotes. That is the tokeniser
 * below. Reaching for SheetJS to read a list of city names would pull in a package whose npm
 * releases stopped at 0.18.5 and which carries prototype-pollution and ReDoS advisories — a bad
 * trade in a KYC product. If binary .xlsx ever becomes a hard requirement, `exceljs` is the
 * maintained choice and should be lazy-imported so it stays out of the main bundle.
 */

/** A problem with one row, or with the sheet as a whole when `row` is null. */
export interface SheetWarning {
  /** 1-based line in the pasted text, as the person sees it in Excel. Null for whole-sheet notes. */
  row: number | null;
  message: string;
}

/**
 * What a sheet turned into.
 *
 * A DISCRIMINATED UNION rather than one object with optional fields: a single column is a plain
 * option list and not a cascade at all, and an unusable sheet has no tree to report counts for.
 * Modelling those as nullable properties would let the dialog render a preview for a result that
 * has nothing in it.
 */
export type CascadeSheetOutcome =
  | { kind: 'error'; message: string }
  | {
      kind: 'options';
      /** One column means one dropdown — imported as plain options, not a cascade. */
      options: string[];
      warnings: SheetWarning[];
      rowsUsed: number;
    }
  | {
      kind: 'cascade';
      cascade: Cascade;
      warnings: SheetWarning[];
      rowsUsed: number;
      /** Total options across every tier. */
      total: number;
      /** Options at each tier — `[1, 36, 752]` for one country, 36 states, 752 districts. */
      perLevel: number[];
    };

/**
 * Row and node ceilings.
 *
 * These exist to keep a mis-paste from locking up the tab, not to police form design. Someone who
 * selects a whole worksheet in Excel can easily paste a million mostly-empty rows; parsing and
 * tree-building them is fast but not free, and the browser has no way to interrupt it. Refusing
 * with a count is a better outcome than a frozen page.
 */
const MAX_ROWS = 20000;
const MAX_NODES = 20000;

/** Per-row warnings listed individually before the rest are summarised. */
const MAX_LISTED_WARNINGS = 20;

/**
 * Splits delimited text into rows of fields, following RFC 4180 quoting.
 *
 * WRITTEN OUT RATHER THAN SPLIT ON `,` AND `\n`, because the naive version is wrong for real
 * exports: `"Ahmedabad, Gujarat"` is one field, `""` inside a quoted field is a literal quote, and
 * a quoted field may contain a line break. Getting any of those wrong shifts every column after it
 * and silently builds the wrong tree, which is worse than failing.
 *
 * A quote only opens a field when it is the FIRST character — `5" pipe` keeps its quote instead of
 * being read as the start of quoting.
 */
function parseDelimited(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let i = 0;

  const endField = () => {
    row.push(field);
    field = '';
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const ch = text[i];

    if (quoted) {
      if (ch === '"') {
        // A doubled quote is one literal quote; a single one closes the field.
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }

    if (ch === '"' && field.length === 0) {
      quoted = true;
      i += 1;
      continue;
    }
    if (ch === delimiter) {
      endField();
      i += 1;
      continue;
    }
    if (ch === '\r') {
      endRow();
      // CRLF counts once. Windows exports and the Excel clipboard both use it.
      i += text[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    if (ch === '\n') {
      endRow();
      i += 1;
      continue;
    }

    field += ch;
    i += 1;
  }

  // Whatever is buffered when the text ends is a final row — a file without a trailing newline
  // would otherwise lose its last line.
  endRow();
  return rows;
}

/**
 * The delimiter the text is using.
 *
 * TAB WINS OUTRIGHT when present, because that is what Excel and Sheets put on the clipboard, and
 * pasting is the primary path. Otherwise the more frequent of comma and semicolon — European Excel
 * writes semicolon-separated CSV, and reading one of those as a single column would look like a
 * broken import rather than a locale difference.
 *
 * Counted across the whole text rather than the header line alone: one quoted comma in a header is
 * enough to mislead a single-line vote, whereas it cannot outweigh a whole file.
 */
function detectDelimiter(text: string): string {
  if (text.includes('\t')) return '\t';
  const commas = (text.match(/,/g) ?? []).length;
  const semicolons = (text.match(/;/g) ?? []).length;
  return semicolons > commas ? ';' : ',';
}

/**
 * A tier's name for a message, by position.
 *
 * The index is always in range where this is called, but saying so with a `!` would make the
 * message the one place a future change could crash the parser. A named fallback costs nothing and
 * keeps "never throws" true by construction rather than by argument.
 */
function levelTitle(levels: readonly CascadeLevel[], index: number): string {
  return levels[index]?.title ?? `Level ${index + 1}`;
}

/** Collapses inner runs of whitespace so `"New  Delhi"` and `"New Delhi"` are one option. */
function tidy(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

/** The key two labels are considered the same option by. */
function dedupeKey(label: string): string {
  return tidy(label).toLowerCase();
}

/** A node under construction, with a map for O(1) sibling lookup while inserting. */
interface DraftNode {
  id: string;
  label: string;
  order: DraftNode[];
  byKey: Map<string, DraftNode>;
}

function newDraft(label: string): DraftNode {
  return { id: newCascadeId('n'), label, order: [], byKey: new Map() };
}

function toNodes(drafts: DraftNode[]): CascadeNode[] {
  return drafts.map((draft) =>
    draft.order.length > 0
      ? { id: draft.id, label: draft.label, children: toNodes(draft.order) }
      : { id: draft.id, label: draft.label },
  );
}

/**
 * Reads a pasted or uploaded sheet.
 *
 * Never throws. Every way a sheet can be wrong comes back as an `error` outcome or a warning, so
 * the dialog can always show something and the caller never needs a try/catch.
 */
export function parseCascadeSheet(text: string): CascadeSheetOutcome {
  if (text.trim().length === 0) {
    return { kind: 'error', message: 'Nothing to import — paste some rows or choose a file.' };
  }

  const delimiter = detectDelimiter(text);
  const raw = parseDelimited(text, delimiter);

  // Fully blank lines carry no information at any position — leading, trailing, or used as a
  // spacer between blocks — so they are dropped before the header is chosen.
  const lines = raw
    .map((cells, index) => ({ cells, line: index + 1 }))
    .filter(({ cells }) => cells.some((cell) => cell.trim().length > 0));

  if (lines.length > MAX_ROWS + 1) {
    return {
      kind: 'error',
      message: `That sheet has ${(lines.length - 1).toLocaleString()} rows. The most that can be imported at once is ${MAX_ROWS.toLocaleString()}.`,
    };
  }

  // Destructured with an explicit guard rather than `lines[0]` after a length check: the guard is
  // what proves to the compiler AND the reader that there is a header, and it splits the data rows
  // off in the same step.
  const [header, ...dataRows] = lines;
  if (header === undefined) {
    return { kind: 'error', message: 'Nothing to import — every row is empty.' };
  }

  const headerCells = header.cells;

  // The header's width is its length with TRAILING blanks trimmed — not its count of non-blank
  // cells. Those differ whenever a header cell in the middle is empty (`Country,,District`), and
  // counting non-blanks there would report 2 columns for a 3-column sheet, quietly shifting every
  // value one tier to the left and building a wrong tree. A trailing blank is a stray delimiter
  // and means nothing; an interior blank is a real column that just went unnamed.
  let columnCount = headerCells.length;
  while (columnCount > 0 && (headerCells[columnCount - 1] ?? '').trim().length === 0) {
    columnCount -= 1;
  }

  if (columnCount === 0) {
    return { kind: 'error', message: 'The first row must name the dropdowns — it is empty.' };
  }
  if (columnCount > MAX_CASCADE_LEVELS) {
    return {
      kind: 'error',
      message: `The sheet has ${columnCount} columns, so it describes ${columnCount} dropdowns. The maximum is ${MAX_CASCADE_LEVELS} — remove ${columnCount - MAX_CASCADE_LEVELS} column${columnCount - MAX_CASCADE_LEVELS === 1 ? '' : 's'} and import again.`,
    };
  }

  const warnings: SheetWarning[] = [];

  if (dataRows.length === 0) {
    return {
      kind: 'error',
      message: 'The sheet has a header row but no data rows underneath it.',
    };
  }

  // ── One column: a plain option list, not a cascade ──
  //
  // Handled here rather than rejected, because "paste my 40 branch names into a dropdown" is the
  // same job with the same sheet, and sending someone away to a different control for it would be
  // arbitrary. The header is the field's own title in that case, so it is not consumed as a level.
  if (columnCount === 1) {
    const seen = new Set<string>();
    const options: string[] = [];
    let duplicates = 0;

    for (const { cells } of dataRows) {
      const label = tidy(cells[0] ?? '');
      if (label.length === 0) continue;
      const key = label.toLowerCase();
      if (seen.has(key)) {
        duplicates += 1;
        continue;
      }
      seen.add(key);
      options.push(label);
    }

    if (options.length === 0) {
      return { kind: 'error', message: 'No options found — the column under the header is empty.' };
    }
    if (duplicates > 0) {
      warnings.push({
        row: null,
        message: `${duplicates} duplicate ${duplicates === 1 ? 'option was' : 'options were'} removed.`,
      });
    }

    return { kind: 'options', options, warnings, rowsUsed: options.length };
  }

  // ── Two or more columns: build the tree ──

  const levels: CascadeLevel[] = Array.from({ length: columnCount }, (_, index) => {
    const title = tidy(headerCells[index] ?? '');
    return {
      id: newCascadeId('lv'),
      title: title.length > 0 ? title : `Level ${index + 1}`,
      // A tier's own name makes a far better prompt than a generic one, and it is free here.
      placeholder: title.length > 0 ? `Select ${title.toLowerCase()}` : 'Select an option',
    };
  });

  const roots: DraftNode[] = [];
  const rootsByKey = new Map<string, DraftNode>();

  /**
   * The last explicit value seen in each column, for forward-fill.
   *
   * FORWARD-FILL IS NOT OPTIONAL. Real sheets are typed the way a person reads them — the country
   * once, the state once, then the districts under it — so most cells in the left-hand columns are
   * blank. Treating a blank as "no value" would reject the majority of genuine uploads.
   */
  const carry: string[] = new Array(columnCount).fill('');

  let rowsUsed = 0;
  let merged = 0;
  let nodeCount = 0;
  let listed = 0;
  let suppressed = 0;

  const warn = (row: number, message: string) => {
    if (listed < MAX_LISTED_WARNINGS) {
      warnings.push({ row, message });
      listed += 1;
    } else {
      suppressed += 1;
    }
  };

  for (const { cells, line } of dataRows) {
    if (cells.length > columnCount && cells.slice(columnCount).some((c) => c.trim().length > 0)) {
      // Reported rather than silently trimmed: an extra populated column usually means the sheet
      // has a stray note beside the data, and the person needs to know it was not imported.
      warn(line, `has ${cells.length} columns — the extra ${cells.length - columnCount} ignored.`);
    }

    // Resolve the row against the carry, and reset everything deeper whenever a column supplies a
    // new explicit value. Without that reset, a new state's blank district cell would inherit the
    // PREVIOUS state's district and quietly attach cities to the wrong branch.
    const values: string[] = [];
    for (let column = 0; column < columnCount; column += 1) {
      const explicit = tidy(cells[column] ?? '');
      if (explicit.length > 0) {
        carry[column] = explicit;
        for (let deeper = column + 1; deeper < columnCount; deeper += 1) {
          carry[deeper] = '';
        }
      }
      values.push(carry[column] ?? '');
    }

    // The path is the leading run of filled columns. A filled column AFTER a blank one is a real
    // hole — a district with no state above it — which cannot be placed in a tree.
    let depth = 0;
    while (depth < columnCount && (values[depth] ?? '').length > 0) depth += 1;

    if (depth === 0) {
      warn(line, `has no value in "${levelTitle(levels, 0)}" — skipped.`);
      continue;
    }
    if (values.slice(depth).some((value) => value.length > 0)) {
      warn(line, `is missing "${levelTitle(levels, depth)}" but has values after it — skipped.`);
      continue;
    }

    // Insert the path, reusing any node that already exists at each tier.
    let siblings = roots;
    let byKey = rootsByKey;
    for (let column = 0; column < depth; column += 1) {
      const label = values[column] ?? '';
      const key = dedupeKey(label);
      let node = byKey.get(key);
      if (node) {
        merged += 1;
      } else {
        if (nodeCount >= MAX_NODES) {
          return {
            kind: 'error',
            message: `That sheet builds more than ${MAX_NODES.toLocaleString()} options. Split it into separate fields, or use a shorter list.`,
          };
        }
        node = newDraft(label);
        byKey.set(key, node);
        siblings.push(node);
        nodeCount += 1;
      }
      siblings = node.order;
      byKey = node.byKey;
    }

    rowsUsed += 1;
  }

  if (nodeCount === 0) {
    return {
      kind: 'error',
      message: 'No options could be read — check that the rows under the header are filled in.',
    };
  }

  if (suppressed > 0) {
    warnings.push({
      row: null,
      message: `${suppressed} further row ${suppressed === 1 ? 'problem was' : 'problems were'} found but not listed.`,
    });
  }
  if (merged > 0) {
    // One aggregate line, not one per occurrence: a 700-row district sheet repeats its state name
    // on nearly every row, and that is the format working as intended, not 700 faults.
    warnings.push({
      row: null,
      message: `${merged.toLocaleString()} repeated ${merged === 1 ? 'value was' : 'values were'} merged into existing options.`,
    });
  }

  const cascade: Cascade = { levels, nodes: toNodes(roots) };
  const perLevel: number[] = [];
  let tier = cascade.nodes;
  while (tier.length > 0) {
    perLevel.push(tier.length);
    tier = tier.flatMap((node) => node.children ?? []);
  }

  return { kind: 'cascade', cascade, warnings, rowsUsed, total: nodeCount, perLevel };
}

/**
 * Adds an imported tree into an existing one, keeping what is already there.
 *
 * WHY MERGE EXISTS AT ALL. Replace covers the first import; merge is what makes the feature
 * maintainable afterwards — adding one state's districts to a list already in use, without
 * re-uploading and re-verifying the whole thing.
 *
 * EXISTING IDS SURVIVE. Options are matched by label, but a matched node keeps ITS OWN id, so
 * answers already recorded against it stay resolvable. Re-importing the same sheet is therefore a
 * no-op rather than a silent renumbering of every option in the form.
 *
 * LEVELS ARE THE EXISTING ONES. Their titles were written by the admin in the builder and may well
 * be better than the sheet's header. Extra tiers in the import are appended, so a two-level cascade
 * can grow to three by importing a three-column sheet.
 *
 * This is import POLICY rather than a tree primitive, which is why it lives here next to the parser
 * instead of in `fieldConfig.ts` with the generic walkers.
 */
export function mergeCascade(existing: Cascade, imported: Cascade): Cascade {
  const mergeNodes = (target: CascadeNode[], source: CascadeNode[]): CascadeNode[] => {
    const result = target.map((node) => ({ ...node }));
    const byKey = new Map(result.map((node) => [dedupeKey(node.label), node]));

    for (const incoming of source) {
      const key = dedupeKey(incoming.label);
      const match = byKey.get(key);

      if (!match) {
        result.push(incoming);
        byKey.set(key, incoming);
        continue;
      }

      if (incoming.children && incoming.children.length > 0) {
        match.children = mergeNodes(match.children ?? [], incoming.children);
      }
    }

    return result;
  };

  return {
    // `slice(existing.levels.length)` and not a concat of both: only tiers the existing cascade
    // does not have yet are taken from the import.
    levels: [...existing.levels, ...imported.levels.slice(existing.levels.length)].slice(
      0,
      MAX_CASCADE_LEVELS,
    ),
    nodes: mergeNodes(existing.nodes, imported.nodes),
  };
}

/**
 * The sample sheet's hierarchy — the deepest one Indian addresses actually use, which is also the
 * reason `MAX_CASCADE_LEVELS` is 5.
 */
const SAMPLE_HEADER = ['Country', 'State', 'District', 'Taluka', 'Village'] as const;

/**
 * Real place names, laid out exactly as somebody would type them.
 *
 * REAL DATA, NOT NUMBERED PLACEHOLDERS. An earlier version generated `Country 1 / State 1 /
 * District 1`, which teaches nothing about the format and reads as a broken file. The point of a
 * sample is to be looked at once and understood, and recognisable names make the inheritance
 * obvious in a way stand-ins never can.
 *
 * The layout demonstrates every part of the format that is not guessable:
 *   • row 1 — a full path, every column filled
 *   • rows 2–3 — only the deeper columns filled, everything above INHERITED from the row above
 *   • rows 4–6 — a new value part-way up, which RESETS the columns beneath it
 *   • row 7 — a second root, starting the chain again
 */
const SAMPLE_ROWS: readonly (readonly string[])[] = [
  ['India', 'Gujarat', 'Ahmedabad', 'Daskroi', 'Bopal'],
  ['', '', '', '', 'Vastrapur'],
  ['', '', '', 'Sanand', 'Sanand'],
  ['', '', 'Vadodara', 'Padra', 'Padra'],
  ['', 'Maharashtra', 'Pune', 'Haveli', 'Kothrud'],
  ['', '', 'Nagpur', 'Nagpur', 'Dharampeth'],
  ['Nepal', 'Bagmati', 'Kathmandu', 'Kathmandu', 'Thamel'],
];

/**
 * A sample sheet, in CSV, sized to the number of levels the field has.
 *
 * SIZED BUT NOT RENAMED. Building the header from the field's own level titles sounded helpful and
 * was not: the values underneath could only ever be `Brand 1`, `Model 1`, because there is no way to
 * know what a brand is called. Real headers over placeholder data is worse than an honest example
 * about geography, and the dialog's format hint already explains that the header names the dropdowns.
 *
 * Rows left entirely blank by the narrowing are dropped, so a two-column sample does not open in
 * Excel full of empty lines.
 */
export function sampleCascadeCsv(levels: readonly CascadeLevel[]): string {
  // Two is the floor — one column is not a cascade — and three the default, so a field whose levels
  // are still untouched gets a sample with real depth in it.
  const width = Math.min(levels.length >= 2 ? levels.length : 3, MAX_CASCADE_LEVELS);

  const quote = (value: string) =>
    /[",;\t\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

  const rows = SAMPLE_ROWS.map((row) => row.slice(0, width)).filter((row) =>
    row.some((cell) => cell.length > 0),
  );

  return [SAMPLE_HEADER.slice(0, width), ...rows]
    .map((row) => row.map(quote).join(','))
    .join('\r\n');
}
